import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Phone, Lightbulb, FileText, Factory, Wrench, PlayCircle, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const Process = () => {
  const processSteps = [
    {
      id: "consultation",
      step: "01",
      icon: Phone,
      title: "Business Consultation",
      description: "Initial discussion about your beverage business goals, budget, and production requirements. We analyze market feasibility and provide expert guidance.",
      deliverables: ["Requirement Analysis", "Feasibility Study", "Budget Planning"]
    },
    {
      id: "planning",
      step: "02",
      icon: FileText,
      title: "Project Planning & Layout",
      description: "Custom plant design based on your space, capacity needs, and production goals. Complete technical drawings and equipment specifications.",
      deliverables: ["Plant Layout Design", "Equipment Selection", "Technical Specifications"]
    },
    {
      id: "manufacturing",
      step: "03",
      icon: Factory,
      title: "Machinery Manufacturing",
      description: "In-house manufacturing of critical equipment with quality materials. Rigorous testing of all machinery before dispatch.",
      deliverables: ["Equipment Fabrication", "Quality Testing", "Documentation"]
    },
    {
      id: "installation",
      step: "04",
      icon: Wrench,
      title: "Installation & Integration",
      description: "On-site installation by experienced technicians. Complete integration of all systems including utilities, automation, and quality control.",
      deliverables: ["Equipment Installation", "System Integration", "Utility Setup"]
    },
    {
      id: "trial",
      step: "05",
      icon: PlayCircle,
      title: "Trial Production",
      description: "Test runs with actual production to ensure quality output. Fine-tuning of all parameters for optimal performance.",
      deliverables: ["Trial Runs", "Quality Checks", "Performance Optimization"]
    },
    {
      id: "training",
      step: "06",
      icon: GraduationCap,
      title: "Training & Support",
      description: "Comprehensive training for your operators and maintenance staff. Ongoing technical support and spare parts supply.",
      deliverables: ["Operator Training", "Documentation", "24/7 Support"]
    }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Our Turnkey Project Process | From Enquiry to Commissioning | Alden Technologies"
        description="See how Alden Technologies executes turnkey water plant projects: consultation, plant design, manufacturing, installation, commissioning, training and after-sales support."
        path="/process"
      />
      <Breadcrumbs items={[{ label: 'Our Process' }]} />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158"
            alt="Beverage Plant Process"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our Turnkey Process
          </h1>
          <p className="text-xl md:text-2xl text-emerald-100 max-w-3xl mx-auto">
            From Idea to Production — We Handle Everything
          </p>
        </div>
      </section>

      {/* Single Point Responsibility */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-block bg-emerald-100 text-emerald-700 px-6 py-3 rounded-full text-sm font-semibold mb-6">
              🎯 Complete Turnkey Execution
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Single Point <span className="text-emerald-600">Responsibility</span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Unlike traditional suppliers who only provide equipment, Alden Technologies takes complete responsibility for your entire beverage plant project. From initial consultation to final commissioning, we manage everything so you can focus on growing your business.
            </p>
            <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 text-left">
              <p className="text-lg text-emerald-900 font-semibold mb-2">
                ✓ No Multiple Vendors - No Coordination Hassles - No Hidden Costs
              </p>
              <p className="text-emerald-800">
                One partner, one contract, one point of contact for your complete beverage plant setup.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              6-Step <span className="text-emerald-600">Turnkey Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Systematic execution for guaranteed results
            </p>
          </div>

          <div className="max-w-6xl mx-auto space-y-12">
            {processSteps.map((process, index) => (
              <Card key={process.id} className="border-2 border-gray-200 hover:border-emerald-500 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-12 gap-8 items-start">
                    {/* Step Number & Icon */}
                    <div className="md:col-span-2 text-center">
                      <div className="w-20 h-20 bg-emerald-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                        {process.step}
                      </div>
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                        <process.icon className="w-8 h-8 text-emerald-600" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="md:col-span-7">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{process.title}</h3>
                      <p className="text-lg text-gray-700 leading-relaxed mb-6">
                        {process.description}
                      </p>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Key Deliverables:</h4>
                        <ul className="space-y-2">
                          {process.deliverables.map((deliverable) => (
                            <li key={deliverable} className="flex items-center gap-2 text-gray-700">
                              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                              <span>{deliverable}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Timeline */}
                    <div className="md:col-span-3 bg-emerald-50 p-6 rounded-lg">
                      <h4 className="font-bold text-emerald-900 mb-2">Timeline</h4>
                      <p className="text-emerald-800 text-sm">
                        {index === 0 && "1-2 weeks"}
                        {index === 1 && "2-3 weeks"}
                        {index === 2 && "6-10 weeks"}
                        {index === 3 && "3-4 weeks"}
                        {index === 4 && "1 week"}
                        {index === 5 && "Ongoing"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Total Timeline */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-emerald-600 text-white px-8 py-4 rounded-lg">
              <p className="text-sm font-medium mb-1">Total Project Timeline</p>
              <p className="text-3xl font-bold">3-6 Months</p>
              <p className="text-sm text-emerald-100 mt-1">From Order to Production-Ready Plant</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Our Process Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Our <span className="text-emerald-600">Process Works</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-emerald-200">
                <CardContent className="p-8">
                  <Lightbulb className="w-12 h-12 text-emerald-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Proven Methodology</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Refined over 200+ successful projects. Our systematic approach eliminates common pitfalls and ensures predictable outcomes.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-emerald-200">
                <CardContent className="p-8">
                  <CheckCircle className="w-12 h-12 text-emerald-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Quality Checkpoints</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Multiple quality gates at each stage ensure the final plant meets international standards and your expectations.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-emerald-200">
                <CardContent className="p-8">
                  <Factory className="w-12 h-12 text-emerald-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-4">In-House Manufacturing</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Critical equipment manufactured in our own facility ensures quality control and faster project completion.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-emerald-200">
                <CardContent className="p-8">
                  <GraduationCap className="w-12 h-12 text-emerald-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Ongoing Support</h3>
                  <p className="text-gray-700 leading-relaxed">
                    24/7 technical support, regular maintenance, spare parts supply - we&apos;re with you for the long term.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Beverage Plant Project?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Let&apos;s discuss your requirements and create a customized project plan
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-10 py-7 text-xl font-semibold">
              Schedule Free Consultation
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Process;
